import { AssetUploadForm } from '@/components/admin/asset-upload';
import { Button } from '@/components/ui/button';
import { ASSET_ACCEPT, assetSubtitle } from '@/lib/ambiance-labels';
import { ambianceFileUrl } from '@/lib/api';
import type { AmbianceAsset, AmbianceAssetKind } from '@matter_extractor/shared';
import { filterAmbianceAssets } from '@matter_extractor/shared';
import { useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * The media library (BUILD-831, maquette « Bibliothèque de médias »): ONE
 * component behind every place a file is chosen - the Ambiance screen, the
 * ambiance bar and the block cards of the openings editor, and the wizard
 * pages when they land.
 *
 * What it replaces is a `<select>` of filenames, and the reason that had to
 * go is not tidiness: choosing a decor from `quai-nuit.webp` vs
 * `couloir-voiture.webp` is choosing a picture without seeing it, and
 * choosing a whistle from `sifflet-2.mp3` is choosing a sound without hearing
 * it. So an image is a THUMBNAIL and a sound is an `<audio>` - the same
 * in-page listening the Ambiance screen has always done, mounted at the
 * moment of the choice rather than beside it.
 *
 * Three decisions worth keeping.
 *
 * **The narrowing is the SHARED pure function**, `filterAmbianceAssets`, the
 * same one `GET /api/ambiance/assets` filters with. The picker holds the
 * library its host already fetched, so it filters locally and answers
 * instantly - but a second implementation of "does this file match what was
 * typed" would disagree on the first accent, and the operator would blame the
 * file rather than the screen.
 *
 * **Confirming is a second gesture.** Clicking a thumbnail SELECTS; the
 * footer button commits. Selecting on click would make a mistaken tap an
 * assignment - and an assignment on the Ambiance screen is a journal line
 * stamped with the immersive session count (I3), which is not something to
 * write by brushing past a picture.
 *
 * **The upload lives inside**, folded, on the shared `AssetUploadForm`: an
 * operator who does not find the file is one click from adding it, without
 * leaving the choice they came to make. Nothing new posts anywhere - it is
 * the EXISTING ambiance upload route, and nothing of an asset ever transits
 * through the openings routes (O4).
 *
 * **And it renders through a PORTAL on `document.body`** (BUILD-875), which is
 * the only thing that makes `z-40` mean what it says.
 *
 * A `position: fixed` box does not escape a STACKING CONTEXT: it is painted
 * inside its ancestor's, and that ancestor paints wherever its own z-index puts
 * it. The openings editor mounts a slot in a column that is `lg:sticky`, and
 * `position: sticky` creates a stacking context on its own - no z-index needed.
 * So the column paints at `z-auto` in the root, the picker's `z-40` only ever
 * ordered it against its siblings inside that column, and the shell's `z-20`
 * bar painted over the modal's whole top row: search, « Téléverser… », the
 * filter chips and « Fermer », all of them under the bar and unclickable
 * (`document.elementFromPoint` on the search field returned a link of the
 * header). The same modal opened from the Ambiance screen, which has no sticky
 * ancestor, was fine - which is why this looked like a screen-specific bug.
 *
 * Raising the z-index would not have fixed it (the trapped context is the
 * ceiling), and raising the BAR's would break I8 - the tester preview and the
 * night station live above it deliberately (`routes/admin.tsx`). The portal
 * puts the modal back in the root stacking context, where the numbers already
 * agree, and it is host-agnostic: every screen that mounts this picker gets it,
 * including the ones that grow a sticky or transformed ancestor tomorrow.
 *
 * It moves the PICKER and nothing else. The preview frame of the openings
 * editor keeps its transformed ancestor, so `RecitPage` stays confined to its
 * dark frame exactly as O10 requires - the two are separate elements and this
 * change never crosses into that subtree.
 */

export interface MediaPickerProps {
  /** What the modal is choosing for - named in its accessible label. */
  title: string;
  /** The slot's kind; `'all'` shows both and offers the filter chips. */
  kind: AmbianceAssetKind | 'all';
  assets: readonly AmbianceAsset[];
  /** What plays behind the slot today; null when nothing does. */
  selectedId: string | null;
  /** What an empty binding falls back on, said by the caller (I9). */
  emptyWording: string;
  busy: boolean;
  /**
   * The French of the last refusal that targeted this slot - an upload or a
   * binding - worded from its code. ONE surface for both: from inside the
   * modal they are the same gesture (« mettre ce fichier là »), and a refused
   * binding that closed the modal silently would look like a success.
   */
  refusal?: string | null;
  /** Commits the choice; null clears the binding. */
  onConfirm: (assetId: string | null) => void;
  onClose: () => void;
  onUpload: (file: File, license: string, tags: string) => void;
}

/** The chips of the header, in the order the maquette shows them. */
const CHIPS: { value: AmbianceAssetKind | 'all'; label: string }[] = [
  { value: 'all', label: 'Tout' },
  { value: 'image', label: 'Images' },
  { value: 'sound', label: 'Sons' },
];

export function MediaPicker({
  title,
  kind,
  assets,
  selectedId,
  emptyWording,
  busy,
  refusal = null,
  onConfirm,
  onClose,
  onUpload,
}: MediaPickerProps) {
  const searchId = useId();
  const [query, setQuery] = useState('');
  // The chip only exists when the slot takes both kinds; when it takes one,
  // the kind is a FACT of the slot and offering to widen it would offer files
  // the assignment route refuses in `KIND_MISMATCH`.
  const [chip, setChip] = useState<AmbianceAssetKind | 'all'>(kind);
  const [selected, setSelected] = useState<string | null>(selectedId);
  const [uploadOpen, setUploadOpen] = useState(false);
  /**
   * How large the library was when the last upload was submitted, or null.
   *
   * It is what tells a SUCCESS from a REFUSAL without an effect and without a
   * second source of truth: the library grew, so the file got in. A refusal
   * leaves the count where it was, and the form stays open holding the entry
   * the operator typed - the rule of BUILD-719. Without this the form stayed
   * open on a success too, and a second click on « Téléverser » posted the
   * same bytes again: ids are random, so nothing would have deduplicated it.
   */
  const [countAtSubmit, setCountAtSubmit] = useState<number | null>(null);
  const uploaded = countAtSubmit !== null && assets.length > countAtSubmit;

  const narrowed = useMemo(
    () =>
      filterAmbianceAssets(assets, {
        q: query,
        ...(chip === 'all' ? {} : { kind: chip }),
      }),
    [assets, query, chip],
  );
  const images = narrowed.filter((asset) => asset.kind === 'image');
  const sounds = narrowed.filter((asset) => asset.kind === 'sound');
  const chosen = assets.find((asset) => asset.id === selected) ?? null;
  const showUpload = uploadOpen && !uploaded;

  return createPortal(
    // A WHITE veil, not a dark one: the ground of this application is white,
    // always (charte). Same construction as `publish-modal.tsx`.
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-white/80 p-5">
      <dialog
        open
        aria-label={title}
        data-testid="media-picker"
        className="relative m-0 flex max-h-[calc(100vh-2.5rem)] w-full max-w-3xl flex-col overflow-hidden rounded-md border-2 border-gray-300 bg-white p-0 shadow-lg"
      >
        <div className="flex flex-wrap items-center gap-3 border-gray-200 border-b px-5 py-3">
          <label htmlFor={searchId} className="sr-only">
            Rechercher un fichier
          </label>
          <input
            id={searchId}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher un fichier…"
            className="h-9 w-56 rounded-md border-2 border-gray-300 px-3 text-sm focus-visible:border-bleu-nuit focus-visible:outline-none"
          />
          {kind === 'all' ? (
            // A `<fieldset>` and not a `role="group"` div: the semantic
            // element is what `useSemanticElements` asks for, and the legend
            // gives the group its name without printing a label nobody needs.
            <fieldset className="flex gap-1 border-0 p-0">
              <legend className="sr-only">Filtrer la bibliothèque</legend>
              {CHIPS.map((entry) => (
                <Button
                  key={entry.value}
                  type="button"
                  size="sm"
                  variant={chip === entry.value ? 'default' : 'outline'}
                  aria-pressed={chip === entry.value}
                  onClick={() => setChip(entry.value)}
                >
                  {entry.label}
                </Button>
              ))}
            </fieldset>
          ) : null}
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setUploadOpen(!showUpload);
                setCountAtSubmit(null);
              }}
            >
              {showUpload ? 'Masquer le téléversement' : 'Téléverser…'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onClose}>
              Fermer
            </Button>
          </div>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {showUpload ? (
            <div className="space-y-2 rounded-md border border-gray-200 p-4">
              <AssetUploadForm
                busy={busy}
                accept={kind === 'all' ? ASSET_ACCEPT.all : ASSET_ACCEPT[kind]}
                compact
                refusal={refusal}
                // The form keeps its entry across a submit - the rule of
                // BUILD-719: a refusal comes back typed and must not cost the
                // operator the license they just typed. It folds only once the
                // library has actually grown, which is `uploaded` above.
                onUpload={(file, license, tags) => {
                  setCountAtSubmit(assets.length);
                  onUpload(file, license, tags);
                }}
                onCancel={() => setUploadOpen(false)}
              />
            </div>
          ) : null}

          {uploaded ? (
            <p className="text-gray-700 text-sm">
              Le fichier est entré dans la bibliothèque : il est ci-dessous, à choisir comme les
              autres.
            </p>
          ) : null}

          {/* A refused BINDING has nowhere else to appear: the modal stays
              open on it, so the sentence sits where the gesture was made. */}
          {refusal === null || showUpload ? null : (
            <p role="alert" className="text-gray-900 text-sm">
              <strong>Refusé :</strong> {refusal}.
            </p>
          )}

          {narrowed.length === 0 ? (
            <p className="text-gray-600 text-sm">
              {assets.length === 0
                ? 'La bibliothèque est vide : téléversez un premier fichier.'
                : 'Aucun fichier ne correspond à cette recherche.'}
            </p>
          ) : null}

          {images.length === 0 ? null : (
            <section aria-label="Images de la bibliothèque" className="space-y-2">
              {kind === 'all' ? <SectionTitle>Images</SectionTitle> : null}
              <ul className="grid grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))] gap-3">
                {images.map((asset) => (
                  <li key={asset.id}>
                    <ImageTile
                      asset={asset}
                      selected={asset.id === selected}
                      onSelect={() => setSelected(asset.id)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {sounds.length === 0 ? null : (
            <section aria-label="Sons de la bibliothèque" className="space-y-2">
              {kind === 'all' ? <SectionTitle>Sons</SectionTitle> : null}
              <ul className="space-y-2">
                {sounds.map((asset) => (
                  <li key={asset.id}>
                    <SoundRow
                      asset={asset}
                      selected={asset.id === selected}
                      onSelect={() => setSelected(asset.id)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p className="text-gray-600 text-xs">
            Licence : facultative, sur un son comme sur une image.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-gray-200 border-t px-5 py-3">
          <p className="text-gray-700 text-sm">
            {chosen === null
              ? `Aucun fichier retenu : ${emptyWording}.`
              : `Sélection : ${chosen.filename}${assetSubtitle(chosen) === '' ? '' : ` - ${assetSubtitle(chosen)}`}`}
          </p>
          <div className="flex gap-2">
            {selectedId === null ? null : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => onConfirm(null)}
              >
                Ne rien assigner
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              disabled={busy || selected === null || selected === selectedId}
              onClick={() => onConfirm(selected)}
            >
              Utiliser ce fichier
            </Button>
          </div>
        </div>
      </dialog>
    </div>,
    document.body,
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <h3 className="font-semibold text-gray-600 text-xs uppercase tracking-wider">{children}</h3>
  );
}

/**
 * One image: the file itself, at the aspect ratio a decor is shown in, so the
 * choice is made on the picture. `loading="lazy"` because a library of thirty
 * decors is thirty full-size files behind one modal.
 */
function ImageTile({
  asset,
  selected,
  onSelect,
}: { asset: AmbianceAsset; selected: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`w-full overflow-hidden rounded-md border-2 text-left ${
        selected ? 'border-bleu-nuit' : 'border-gray-200 hover:border-gray-400'
      }`}
    >
      <img
        src={ambianceFileUrl(asset.id)}
        alt={asset.filename}
        loading="lazy"
        className="aspect-[16/10] w-full bg-gray-50 object-cover"
      />
      <span className="block px-2.5 py-2">
        <span className="block truncate font-semibold text-gray-800 text-sm">{asset.filename}</span>
        <span className="block text-gray-600 text-xs">{assetSubtitle(asset)}</span>
      </span>
    </button>
  );
}

/**
 * One sound: its name, its duration, and a native `<audio controls>` beside
 * it. A native player rather than the maquette's single play button, and it
 * is a choice - the operator needs to scrub a two-minute loop, not trigger
 * it, and a custom transport would be a second audio state machine to keep in
 * step with the one the Ambiance screen already has.
 *
 * The row is a button and the player sits OUTSIDE it: nesting the controls
 * inside would make every press on « play » a selection too.
 */
function SoundRow({
  asset,
  selected,
  onSelect,
}: { asset: AmbianceAsset; selected: boolean; onSelect: () => void }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-3 rounded-md border-2 p-2.5 ${
        selected ? 'border-bleu-nuit' : 'border-gray-200'
      }`}
    >
      <button
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        className="min-w-40 flex-1 text-left"
      >
        <span className="block truncate font-semibold text-gray-800 text-sm">{asset.filename}</span>
        <span className="block text-gray-600 text-xs">{assetSubtitle(asset)}</span>
      </button>
      <audio
        controls
        preload="none"
        src={ambianceFileUrl(asset.id)}
        className="h-9"
        aria-label={`Écouter ${asset.filename}`}
      >
        <track kind="captions" />
      </audio>
    </div>
  );
}
